import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Input, Label } from '@/components/ui/Input';
import { AutocompleteSelect } from '@/components/Autocomplete';
import { apiClient } from '@/lib/api-client';
import { useWizardStore } from '../lib/wizard-store';

// ── API types ────────────────────────────────────────────────────────────────

export interface BigcallWorkspace {
  id: string;
  name: string;
  campaign_count: number;
}

export interface BigcallCampaign {
  id: string;
  name: string;
  status: string;
  lead_count: number;
}

// ── API fetchers ─────────────────────────────────────────────────────────────

async function fetchBigcallWorkspaces(q: string): Promise<BigcallWorkspace[]> {
  const params: Record<string, string> = {};
  if (q) params.q = q;
  const { data } = await apiClient.get<BigcallWorkspace[]>('/v1/external/bigcall/workspaces', {
    params,
  });
  return data;
}

async function fetchBigcallCampaigns(workspaceId: string, q: string): Promise<BigcallCampaign[]> {
  const params: Record<string, string> = { workspace_id: workspaceId };
  if (q) params.q = q;
  const { data } = await apiClient.get<BigcallCampaign[]>('/v1/external/bigcall/campaigns', {
    params,
  });
  return data;
}

// ── W6 Step ──────────────────────────────────────────────────────────────────

export function W6_BigcallSync() {
  const {
    bigcall_workspace_id,
    bigcall_campaign_mode,
    bigcall_campaign_id,
    new_bigcall_campaign_name,
    setBigcallWorkspaceId,
    setBigcallCampaignMode,
    setBigcallCampaignId,
    setNewBigcallCampaignName,
  } = useWizardStore();

  return (
    <Card>
      <CardHeader
        title="Sync BIG.call"
        subtitle="Liez cette campagne à un workspace BIG.call et définissez la campagne d'appels associée."
      />
      <CardContent className="space-y-5" data-testid="w6-bigcall-sync-section">
        <div className="space-y-1" data-testid="workspace-field">
          <Label>Workspace BIG.call (= client / projet)</Label>
          <AutocompleteSelect<BigcallWorkspace>
            value={bigcall_workspace_id}
            onSelect={(id) => {
              setBigcallWorkspaceId(id);
              setBigcallCampaignId(null);
            }}
            onSearch={fetchBigcallWorkspaces}
            renderItem={(ws) => `${ws.name} (${ws.id})`}
            getId={(ws) => ws.id}
            placeholder="Rechercher un workspace…"
            queryKey="bigcall-workspaces"
            data-testid="workspace-autocomplete"
          />
        </div>

        <div className="space-y-3" data-testid="campaign-field">
          <Label>Campagne BIG.call dans ce workspace</Label>

          <div className="flex flex-col gap-2" data-testid="campaign-mode-radio">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="bigcall_campaign_mode"
                value="create_new"
                checked={bigcall_campaign_mode === 'create_new'}
                onChange={() => {
                  setBigcallCampaignMode('create_new');
                  setBigcallCampaignId(null);
                }}
                data-testid="mode-create-new"
              />
              Créer une nouvelle campagne
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="bigcall_campaign_mode"
                value="use_existing"
                checked={bigcall_campaign_mode === 'use_existing'}
                onChange={() => setBigcallCampaignMode('use_existing')}
                data-testid="mode-use-existing"
              />
              Utiliser une campagne existante
            </label>
          </div>

          {bigcall_campaign_mode === 'create_new' && (
            <div className="space-y-1" data-testid="new-campaign-name-field">
              <Label>Nom de la nouvelle campagne BIG.call</Label>
              <Input
                value={new_bigcall_campaign_name}
                onChange={(e) => setNewBigcallCampaignName(e.target.value)}
                placeholder="Auto-généré : <campaign.name> (Q4 2026)"
                data-testid="new-campaign-name-input"
              />
            </div>
          )}

          {bigcall_campaign_mode === 'use_existing' && (
            <div className="space-y-1" data-testid="existing-campaign-field">
              <Label>Campagne existante</Label>
              <AutocompleteSelect<BigcallCampaign>
                value={bigcall_campaign_id ?? ''}
                onSelect={(id) => setBigcallCampaignId(id || null)}
                onSearch={(q) => fetchBigcallCampaigns(bigcall_workspace_id, q)}
                renderItem={(c) => `${c.name} — ${c.lead_count} leads`}
                getId={(c) => c.id}
                placeholder="Rechercher une campagne…"
                disabled={!bigcall_workspace_id}
                queryKey={`bigcall-campaigns-${bigcall_workspace_id}`}
                data-testid="campaign-autocomplete"
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
